import { StorageManager } from 'src/app/common/storage-manager.class';
import {
    Form,
    FormButton,
    FormField,
    FormSection,
    FormTable,
    FormType,
    DropdownData,
    InputType,
    RepeatableComputeContext,
    RepeatableSubField,
    RepeatableSubFieldContext,
    RepeatableSubFieldDependencyContext,
} from '@fitogest/reactive-form-builder/interfaces';

import { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { Uso } from 'src/app/models/productos/uso.model';

import { TratamientosEnvironmentDefinition } from '../../environment-variables';
import { TratamientosListsDefinition } from './list-variables';

type RepeatableDropdownLoader = (
    ctx: RepeatableSubFieldDependencyContext
) => void | DropdownData<unknown> | Promise<void | DropdownData<unknown>>;

export class TratamientosFormDefinition {

    public formType!: FormType;

    public env: TratamientosEnvironmentDefinition = new TratamientosEnvironmentDefinition();

    public lists: TratamientosListsDefinition = new TratamientosListsDefinition();

    public formFields!: Form<TratamientosModel>;

    public fieldNames: FormField<any>[] = [];

    public fieldLabels: string[] = [];

    public cliente = StorageManager.getClient();

    public usuario = StorageManager.getUser();

    public hideDosisAplicada: boolean = true;

    public hideDosisCuaderno: boolean = StorageManager.checkHiddenDosisCuaderno();

    /**
     * The container component injects the resolvers once dropdown data is ready.
     */
    public usosResolver: (producto: ProductosModel | null) => DropdownData<Uso> = (): DropdownData<Uso> => {
        return this.emptyDropdown<Uso>('Uso...');
    };

    public agentesResolver: (
        producto: ProductosModel | null,
        uso: Uso | null
    ) => DropdownData<Uso> = (): DropdownData<Uso> => {
        return this.emptyDropdown<Uso>('Agente...');
    };

    public loadUsosData: RepeatableDropdownLoader = (): DropdownData<unknown> => {
        return this.emptyDropdown<Uso>('Uso...') as DropdownData<unknown>;
    };

    public loadAgentesData: RepeatableDropdownLoader = (): DropdownData<unknown> => {
        return this.emptyDropdown<Uso>('Agente...') as DropdownData<unknown>;
    };

    public patchUnitsAfterAgentChange: (ctx: RepeatableSubFieldDependencyContext) => Record<string, unknown> = () => {
        return {};
    };

    public patchAppliedDoseAfterNotebookDoseChange: (ctx: RepeatableSubFieldDependencyContext) => Record<string, unknown> = () => {
        return {};
    };

    public model: TratamientosModel = {
        id_cliente: String(this.cliente?.id ?? ''),
        id_usuario: String(this.usuario?.id ?? ''),
        nombre: '',
        activo: true,
        cubierta: false,
        ids_productos: '',
        uds_medida: '',
        uds_medida_cuaderno: '',
        ids_usos: '',
        ids_agentes: '',
        nombres_usos: '',
        nombres_agentes: '',
        cantidades_cuaderno: '',
        cantidades_real: '',
        ocultar_en_receta: ''
    };

    public tag = '';

    private readonly productById = new Map<string, ProductosModel>();

    public setProductIndex(products: ProductosModel[]): void {
        this.productById.clear();

        for (const product of products) {
            const productId = String(product.id ?? '').trim();

            if (productId !== '') {
                this.productById.set(productId, product);
            }
        }
    }

    private lastSyncedDosisCuaderno = new Map<number, string>();

    public constructor(
        type: FormType,
        model: TratamientosModel,
        table: FormTable | null
    ) {
        this.formType = +type;
        this.model = {
            ...this.model,
            ...model
        };

        this.formFields = {
            name: 'tratamientos',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table,
            uploadButton: {
                isEnabled: false,
                label: ''
            },
            button: this.getHeaderButtons(),
            signed: false
        };
    }

    public setProductosDataSource(endpoint: unknown): void {
        const subField = this.findRepeatableSubField('producto');

        if (!subField) {
            return;
        }

        subField.dataSource = {
            endpoint,
            cacheKey: 'tratamientos-productos',
            distinctBy: 'id',
            mapOption: (item: unknown) => {
                const product = item as ProductosModel;

                return {
                    label: this.buildProductDropdownLabel(product),
                    value: this.normalizeProductForDropdown(product),
                    raw: product,
                };
            },
        };
    }

    private findRepeatableSubField(key: string): RepeatableSubField | null {
        for (const section of this.formFields.sections) {
            for (const field of section.fields) {
                const subFields = field.input.repeatableGroupConfiguration?.subFields ?? [];
                const found = subFields.find((subField) => subField.key === key);

                if (found) {
                    return found;
                }
            }
        }

        return null;
    }

    private buildProductDropdownLabel(product: ProductosModel): string {
        const productName = String(product.nombre ?? '').trim().toUpperCase();

        if (String(product.tipo ?? '').trim().toLowerCase() !== 'fitosanitario') {
            return productName;
        }

        const registrationNumber = String(product.num_registro ?? '').trim();

        return registrationNumber === '' ? productName : `[${registrationNumber}] ${productName}`;
    }

    private normalizeProductForDropdown(product: ProductosModel): ProductosModel {
        return {
            ...product,
            nombre: String(product.nombre ?? '').trim().toUpperCase(),
        };
    }

    private getHeaderButtons(): FormButton[] {
        return [
            {
                type: 'routerLink',
                label: 'Crear Producto Fitosanitario',
                icon: 'fa fa-plus',
                class: 'btn btn-primary',
                route: ['dashboard', 'crear-producto-fitosanitario'],
                isVisible: this.formType < FormType.DELETION
            }
        ];
    }

    private getHeader(): string {
        switch (this.formType) {
            case FormType.EDITION:
                return 'Editar Tratamiento';
            case FormType.DUPLICATION:
                return 'Duplicar Tratamiento';
            case FormType.DELETION:
                return 'Eliminar Tratamiento';
            case FormType.INFORMATIVE:
                return 'Detalle del Tratamiento';
            case FormType.EXPORT:
                return 'Exportar Tratamientos';
            default:
                return 'Crear Tratamiento';
        }
    }

    public getFormSections(): FormSection<any>[] {
        return [
            {
                name: 'Datos generales',
                fields: this.getGeneralFields(),
                width: 12,
                isVisible: true,
                order: 1
            },
            {
                name: 'Líneas',
                fields: this.getLineFields(),
                width: 12,
                isVisible: true,
                order: 2
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 3
            }
        ];
    }

    private getGeneralFields(): FormField<any>[] {
        const generalFields: FormField<any>[] = [
            {
                name: 'nombre',
                vinculatedFields: undefined,
                label: 'Nombre',
                input: {
                    type: this.formType >= FormType.DELETION
                        ? InputType.ONLY_TEXT
                        : InputType.TEXT,
                    isEditable: this.formType < FormType.DELETION
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: this.env.appName === 'avafitogest',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'activo',
                vinculatedFields: undefined,
                label: 'Activo',
                input: {
                    type: this.formType >= FormType.DELETION
                        ? InputType.ONLY_TEXT
                        : InputType.SWITCH,
                    isEditable: this.formType < FormType.DELETION
                },
                updateValue: (): string | number => {
                    if (this.formType < FormType.DELETION) {
                        return +(this.model.activo ?? 0);
                    }

                    return this.model.activo ? 'Sí' : 'No';
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showAplicadoresActivo
                },
                order: 2
            },
            {
                name: 'cubierta',
                vinculatedFields: undefined,
                label: 'Cubierta',
                input: {
                    type: this.formType >= FormType.DELETION
                        ? InputType.ONLY_TEXT
                        : InputType.SWITCH,
                    isEditable: this.formType < FormType.DELETION
                },
                updateValue: (): string | number => {
                    if (this.formType < FormType.DELETION) {
                        return +(this.model.cubierta ?? 0);
                    }

                    return this.model.cubierta ? 'Sí' : 'No';
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'avafitogest'
                },
                order: 3
            },
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: this.env.clienteName,
                input: {
                    type: this.formType >= FormType.DELETION
                        ? InputType.ONLY_TEXT
                        : InputType.DROPDOWN_SEARCH,
                    data: this.lists.clientes,
                    isEditable: this.formType < FormType.DELETION,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.hasClients && this.env.userCanSeeClientes && this.formType === FormType.DUPLICATION
                },
                order: 4
            }
        ];

        this.fieldNames = this.fieldNames.concat(
            generalFields.filter((it) => it.conditions.isVisible)
        );

        this.fieldLabels = this.fieldLabels.concat(
            generalFields.filter((it) => it.conditions.isVisible).map((it) => it.label)
        );

        return generalFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getLineFields(): FormField<any>[] {
        const fields: FormField<any>[] = [
            {
                name: 'lineas_group',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.REPEATABLE_GROUP,
                    isEditable: this.formType < FormType.DELETION,
                    repeatableGroupConfiguration: {
                        rows: this.getRepeatableRowsLimit(),
                        addButtonLabel: 'Añadir producto',
                        bindings: this.getRepeatableBindings(),
                        serialization: {
                            separator: ';'
                        },
                        legacyAggregateSerialization: {
                            trimTrailingEmptyRows: true
                        },
                        subFields: this.getRepeatableSubFields()
                    }
                },
                width: 12,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            }
        ];

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<any>[] {
        return [
            this.hiddenField('id'),
            this.hiddenField('id_usuario')
        ];
    }

    private getRepeatableBindings(): Record<string, string> {
        return {
            producto: 'ids_productos',
            uso: 'ids_usos',
            usoNombre: 'nombres_usos',
            agente: 'ids_agentes',
            agenteNombre: 'nombres_agentes',
            dosisCuaderno: 'cantidades_cuaderno',
            dosisReal: 'cantidades_real',
            unidadMedida: 'uds_medida',
            unidadMedidaCuaderno: 'uds_medida_cuaderno',
            ocultarReceta: 'ocultar_en_receta'
        };
    }

    private getRepeatableSubFields(): RepeatableSubField[] {
        const isEditable = this.formType < FormType.DELETION;

        return [
            {
                key: 'producto',
                label: 'Producto',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable,
                    data: this.lists.productos,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                }
            },
            {
                key: 'uso',
                label: 'Uso/' + this.env.hasPlantas ? 'Especie' : 'Cultivo',
                dependsOn: 'producto',
                dependencyBehaviour: {
                    clearOnParentChange: true,
                    autoSelect: 'singleOption',
                    keepCurrentValueIfStillAvailable: true
                },
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable,
                    data: this.lists.usosPlaceholder,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        controlValueKey: 'id',
                        backendValueKey: 'id',
                        rawIdentityKeys: ['id', 'id_uso']
                    }
                },
                loadData: (ctx: RepeatableSubFieldDependencyContext) => this.loadUsosData(ctx),
                getData: (ctx: RepeatableSubFieldContext): DropdownData<unknown> => {
                    const producto = this.resolveProductoFromRow(ctx);

                    return this.usosResolver(producto) as DropdownData<unknown>;
                }
            },
            {
                key: 'usoNombre',
                label: 'Uso',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false
                },
                isVisible: (): boolean => false,
                computeValue: (ctx: RepeatableComputeContext): string => {
                    const uso = this.resolveUsoFromRows(ctx.rows, ctx.rowIndex);
                    const currentLabel = String(ctx.rows[ctx.rowIndex]?.['usoNombre'] ?? '').trim();

                    return String(uso?.uso ?? currentLabel);
                }
            },
            {
                key: 'agente',
                label: 'Agente/Plaga',
                dependsOn: ['producto', 'uso'],
                dependencyBehaviour: {
                    clearOnParentChange: true,
                    autoSelect: 'singleOption',
                    keepCurrentValueIfStillAvailable: true
                },
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable,
                    data: this.lists.agentesPlaceholder,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        controlValueKey: 'id',
                        backendValueKey: 'id',
                        rawIdentityKeys: ['id', 'id_uso']
                    }
                },
                loadData: (ctx: RepeatableSubFieldDependencyContext) => this.loadAgentesData(ctx),
                getData: (ctx: RepeatableSubFieldContext): DropdownData<unknown> => {
                    const producto = this.resolveProductoFromRow(ctx);
                    const uso = this.resolveUsoFromRow(ctx);

                    return this.agentesResolver(producto, uso) as DropdownData<unknown>;
                }
            },
            {
                key: 'agenteNombre',
                label: 'Agente',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false
                },
                isVisible: (): boolean => false,
                computeValue: (ctx: RepeatableComputeContext): string => {
                    const agente = this.resolveAgenteFromRows(ctx.rows, ctx.rowIndex);
                    const currentLabel = String(ctx.rows[ctx.rowIndex]?.['agenteNombre'] ?? '').trim();

                    return String(agente?.agente ?? currentLabel);
                }
            },
            {
                key: 'dosisCuaderno',
                label: this.getCuadernoQuantityLabel(),
                input: {
                    type: isEditable ? InputType.NUMERIC : InputType.ONLY_TEXT,
                    isEditable
                },
                isVisible: (): boolean => !this.hideDosisCuaderno
            },
            {
                key: 'unidadMedidaCuaderno',
                label: this.getCuadernoMeasureLabel(),
                dependsOn: ['agente'],
                dependencyBehaviour: { clearOnParentChange: false },
                onDependencyChange: (ctx: RepeatableSubFieldDependencyContext) =>
                    this.patchUnitsAfterAgentChange(ctx),
                input: {
                    type: this.env.showUnidadesMedidaSIEX
                        ? InputType.DROPDOWN
                        : InputType.TEXT,
                    isEditable: isEditable && this.env.showUnidadesMedidaSIEX,
                    data: this.lists.unidadesMedida
                },
                isVisible: (): boolean => this.shouldShowCuadernoMeasureField(),
                computeValue: (ctx: RepeatableComputeContext): string => {
                    return this.resolveMeasureValue(
                        ctx.rows,
                        ctx.rowIndex,
                        'unidadMedidaCuaderno',
                        () => this.resolveAutomaticBaseUnit(ctx.rows, ctx.rowIndex)
                    );
                }
            },
            {
                key: 'dosisReal',
                label: 'C. Aplicada',
                dependsOn: ['dosisCuaderno'],
                dependencyBehaviour: { clearOnParentChange: false },
                onDependencyChange: (ctx: RepeatableSubFieldDependencyContext) =>
                    this.patchAppliedDoseAfterNotebookDoseChange(ctx),
                input: {
                    type: InputType.TEXT,
                    isEditable: true
                },
                isVisible: (): boolean => !this.hideDosisAplicada,
                computeValue: (ctx: RepeatableComputeContext): string => {
                    const row = ctx.rows[ctx.rowIndex] ?? {};
                    const dosisCuaderno = String(row['dosisCuaderno'] ?? '').trim();
                    const dosisReal = String(row['dosisReal'] ?? '').trim();
                    const previousCuaderno = this.lastSyncedDosisCuaderno.get(ctx.rowIndex);

                    this.lastSyncedDosisCuaderno.set(ctx.rowIndex, dosisCuaderno);

                    if (dosisReal === '' || dosisReal === previousCuaderno) {
                        return dosisCuaderno;
                    }

                    return dosisReal;
                }
            },
            {
                key: 'unidadMedida',
                label: this.getAppliedMeasureLabel(),
                dependsOn: ['agente'],
                dependencyBehaviour: { clearOnParentChange: false },
                onDependencyChange: (ctx: RepeatableSubFieldDependencyContext) =>
                    this.patchUnitsAfterAgentChange(ctx),
                input: {
                    type: this.env.showUnidadesMedidaSIEX
                        ? InputType.DROPDOWN
                        : InputType.TEXT,
                    isEditable: isEditable && this.env.showUnidadesMedidaSIEX,
                    data: this.lists.unidadesMedida
                },
                isVisible: (): boolean => this.shouldShowAppliedMeasureField(),
                computeValue: (ctx: RepeatableComputeContext): string => {
                    return this.resolveMeasureValue(
                        ctx.rows,
                        ctx.rowIndex,
                        'unidadMedida',
                        () => this.resolveAutomaticDoseUnit(ctx.rows, ctx.rowIndex)
                    );
                }
            },
            {
                key: 'unidadMedidaProducto',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false
                },
                isVisible: (): boolean => false,
                computeValue: (ctx: RepeatableComputeContext): string => {
                    return String(ctx.rows[ctx.rowIndex]?.['producto'] ?? '').trim();
                }
            },
            {
                key: 'ocultarReceta',
                label: 'Ocultar en receta',
                input: {
                    type: isEditable ? InputType.SWITCH : InputType.ONLY_TEXT,
                    isEditable
                },
                isVisible: (): boolean => this.env.showTratamientosRecetaSwitch,
                computeValue: (ctx: RepeatableComputeContext): string => {
                    const row = ctx.rows[ctx.rowIndex] ?? {};
                    const value = String(row['ocultarReceta'] ?? '').trim().toLowerCase();

                    if (isEditable) {
                        return value;
                    }

                    return ['1', 'true'].includes(value) ? 'Sí' : 'No';
                }
            }
        ];
    }

    private getRepeatableRowsLimit(): number {
        return this.env.appName === 'hernandorena' ? 7 : 15;
    }

    private getCuadernoQuantityLabel(): string {
        if (this.env.appName === 'hernandorena') {
            return 'C. Cuaderno';
        }

        return this.env.enableLegalMode || this.env.showUnidadesMedidaSIEX ? 'Dosis' : 'C. Cuaderno';
    }

    private getCuadernoMeasureLabel(): string {
        if (this.env.appName === 'hernandorena') {
            return 'Ud. cuaderno';
        }

        return this.env.showUnidadesMedidaSIEX ? 'Uds. medida' : 'Uds. cuaderno';
    }

    private getAppliedMeasureLabel(): string {
        return 'Ud. aplicada';
    }

    private shouldShowCuadernoMeasureField(): boolean {
        return this.env.showTratamientosUnidades
            || this.env.showUdsCuaderno
            || this.env.showUnidadesMedidaSIEX
            || this.env.appName === 'hernandorena';
    }

    private shouldShowAppliedMeasureField(): boolean {
        return this.env.appName === 'hernandorena' && !this.hideDosisAplicada;
    }

    private resolveMeasureValue(
        rows: Record<string, string>[],
        rowIndex: number,
        unitField: string,
        resolveAutomaticUnit: () => string
    ): string {
        const row = rows[rowIndex] ?? {};
        const currentValue = String(row[unitField] ?? '').trim();
        const currentProductId = String(row['producto'] ?? '').trim();
        const previousProductId = String(row['unidadMedidaProducto'] ?? '').trim();

        if (currentProductId === '') {
            return '';
        }

        if (currentValue !== '' && (previousProductId === '' || previousProductId === currentProductId)) {
            return currentValue;
        }

        return resolveAutomaticUnit();
    }

    private resolveAutomaticBaseUnit(rows: Record<string, string>[], rowIndex: number): string {
        const producto = this.resolveProductoFromRows(rows, rowIndex);
        const agente = this.resolveAgenteFromRows(rows, rowIndex);

        return this.normalizeBaseUnit(
            producto?.unidades
            ?? producto?.uds_medida
            ?? agente?.unidad_medida
            ?? ''
        );
    }

    private resolveAutomaticDoseUnit(rows: Record<string, string>[], rowIndex: number): string {
        const producto = this.resolveProductoFromRows(rows, rowIndex);
        const agente = this.resolveAgenteFromRows(rows, rowIndex);

        return this.normalizeDoseUnit(
            producto?.unidades
            ?? producto?.uds_medida
            ?? agente?.unidad_medida
            ?? ''
        );
    }

    private hiddenField(
        name: string,
        vinculatedFields?: string[],
        updateValue?: FormField<any>['updateValue']
    ): FormField<any> {
        const field: FormField<any> = {
            name,
            vinculatedFields,
            label: '',
            input: {
                type: InputType.HIDDEN,
                isEditable: true
            },
            width: 1,
            conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true
            },
            order: 999
        };

        if (updateValue) {
            field.updateValue = updateValue;
        }

        return field;
    }

    private emptyDropdown<T>(label: string): DropdownData<T> {
        return {
            values: [{ label, value: null }],
            filtered: [{ label, value: null }]
        };
    }

    private resolveProductoFromRow(ctx: RepeatableSubFieldContext): ProductosModel | null {
        const raw = ctx.row['producto'];

        if (raw != null && typeof raw === 'object') {
            return raw as ProductosModel;
        }

        return this.resolveProductoById(raw);
    }

    private resolveProductoFromRows(rows: Record<string, string>[], rowIndex: number): ProductosModel | null {
        return this.resolveProductoById(rows[rowIndex]?.['producto'] ?? '');
    }

    private resolveProductoById(raw: unknown): ProductosModel | null {
        const id = String(raw ?? '').trim();

        if (id === '') {
            return null;
        }

        return this.productById.get(id) ?? null;
    }

    private resolveUsoFromRow(ctx: RepeatableSubFieldContext): Uso | null {
        const raw = ctx.row['uso'];

        if (raw != null && typeof raw === 'object') {
            return raw as Uso;
        }

        const producto = this.resolveProductoFromRow(ctx);
        const options = this.usosResolver(producto);
        const usoNombre = String(ctx.row['usoNombre'] ?? '').trim();
        const foundByName = this.findUsoByName(options, usoNombre);

        if (foundByName) {
            return foundByName;
        }

        const id = String(raw ?? '').trim();

        if (id === '') {
            return null;
        }

        return (
            options.values
                .map((item) => item.value as Uso | null)
                .filter((item): item is Uso => item !== null)
                .find((item) => String(item.id ?? '') === id)
            ?? null
        );
    }

    private resolveUsoFromRows(rows: Record<string, string>[], rowIndex: number): Uso | null {
        const producto = this.resolveProductoFromRows(rows, rowIndex);
        const options = this.usosResolver(producto);
        const usoNombre = String(rows[rowIndex]?.['usoNombre'] ?? '').trim();
        const foundByName = this.findUsoByName(options, usoNombre);

        if (foundByName) {
            return foundByName;
        }

        const raw = rows[rowIndex]?.['uso'] ?? '';
        const id = String(raw).trim();

        if (id === '') {
            return null;
        }

        return (
            options.values
                .map((item) => item.value as Uso | null)
                .filter((item): item is Uso => item !== null)
                .find((item) => String(item.id ?? '') === id)
            ?? null
        );
    }

    private resolveAgenteFromRows(rows: Record<string, string>[], rowIndex: number): Uso | null {
        const producto = this.resolveProductoFromRows(rows, rowIndex);
        const uso = this.resolveUsoFromRows(rows, rowIndex);
        const options = this.agentesResolver(producto, uso);
        const agenteNombre = String(rows[rowIndex]?.['agenteNombre'] ?? '').trim();
        const foundByName = this.findAgenteByName(options, agenteNombre);

        if (foundByName) {
            return foundByName;
        }

        const raw = rows[rowIndex]?.['agente'] ?? '';
        const id = String(raw).trim();

        if (id === '') {
            return null;
        }

        return (
            options.values
                .map((item) => item.value as Uso | null)
                .filter((item): item is Uso => item !== null)
                .find((item) => String(item.id ?? '') === id)
            ?? null
        );
    }

    private normalizeOptionLabel(value: unknown): string {
        return String(value ?? '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    private findUsoByName(options: DropdownData<Uso>, usoLabel: string): Uso | null {
        const normalizedLabel = this.normalizeOptionLabel(usoLabel);

        if (normalizedLabel === '') {
            return null;
        }

        return (
            options.values
                .map((item) => item.value as Uso | null)
                .filter((item): item is Uso => item !== null)
                .find((item) => this.normalizeOptionLabel(item.uso) === normalizedLabel)
            ?? null
        );
    }

    private findAgenteByName(options: DropdownData<Uso>, agenteLabel: string): Uso | null {
        const normalizedLabel = this.normalizeOptionLabel(agenteLabel);

        if (normalizedLabel === '') {
            return null;
        }

        return (
            options.values
                .map((item) => item.value as Uso | null)
                .filter((item): item is Uso => item !== null)
                .find((item) => this.normalizeOptionLabel(item.agente) === normalizedLabel)
            ?? null
        );
    }

    private normalizeBaseUnit(rawUnit: string | number | boolean | undefined): string {
        const unit = String(rawUnit ?? '').trim().toLowerCase();

        if (['kg', 'kilo', 'kilos', 'kilogramo', 'kilogramos'].includes(unit)) {
            return 'kg';
        }

        if (['l', 'litro', 'litros'].includes(unit)) {
            return 'L';
        }

        if (unit === 'kg/ha') {
            return 'kg';
        }

        if (unit === 'l/ha') {
            return 'L';
        }

        return '';
    }

    private normalizeDoseUnit(rawUnit: string | number | boolean | undefined): string {
        const unit = String(rawUnit ?? '').trim().toLowerCase();

        if (['kg', 'kilo', 'kilos', 'kilogramo', 'kilogramos', 'kg/ha'].includes(unit)) {
            return 'kg/ha';
        }

        if (['l', 'litro', 'litros', 'l/ha'].includes(unit)) {
            return 'L/ha';
        }

        return '';
    }
}
